import React from "react";
import { useTranslation } from "react-i18next";
import { Shield, ArrowRight, Clock, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtMoney } from "../../lib/money";

/**
 * QENVA · Recommandation Guardian™ — Priorité absolue aujourd'hui.
 *
 * Single full-width card with a left rose-red rail signaling urgency.
 * Displays the top recommended action of the day with impact, time,
 * confidence, and an action CTA.
 */

export default function PriorityRecommendationCard({ recommendation, onAction, testId = "priority-recommendation-card" }) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();

  if (!recommendation) {
    return (
      <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 flex items-center gap-4" data-testid={`${testId}-empty`}>
        <div className="w-11 h-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-600">
          <Shield size={20} strokeWidth={1.75} />
        </div>
        <div>
          <p className="font-display font-bold text-qenva-navy text-[15px]">{t("priority_recommendation.empty_title")}</p>
          <p className="text-zinc-500 text-[12.5px]">{t("priority_recommendation.empty_subtitle")}</p>
        </div>
      </section>
    );
  }

  const handleClick = () => {
    if (onAction) onAction(recommendation);
    else if (recommendation.alert_id) navigate(`/alerts?id=${recommendation.alert_id}`);
  };

  const severity = recommendation.severity || "High";
  const isCritical = severity === "Critical";

  return (
    <section
      className="relative rounded-2xl border border-zinc-200 bg-white overflow-hidden flex items-stretch"
      data-testid={testId}
    >
      <div className={`w-1 shrink-0 ${isCritical ? "bg-rose-500" : "bg-amber-500"}`} />
      <div className="flex-1 p-6 flex items-center gap-5 flex-wrap">
        <div className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 ${
          isCritical ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"
        }`}>
          <Shield size={22} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <p
            className={`text-[10.5px] uppercase tracking-[0.16em] font-bold mb-1 ${
              isCritical ? "text-rose-600" : "text-amber-700"
            }`}
            data-testid={`${testId}-kicker`}
          >
            {t("priority_recommendation.kicker")}
          </p>
          <h3 className="font-display font-bold text-qenva-navy text-[18px] tracking-tight leading-tight" data-testid={`${testId}-title`}>
            {recommendation.title}
          </h3>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-[12px]" data-testid={`${testId}-meta`}>
            {recommendation.impact_amount != null && (
              <Stat label={t("priority_recommendation.impact")} value={fmtMoney(recommendation.impact_amount, recommendation.impact_currency)} tone="warning" testId={`${testId}-impact`} />
            )}
            <Stat
              label={t("priority_recommendation.time")}
              value={`${recommendation.estimated_time_min || 15} min`}
              tone="neutral"
              icon={Clock}
              testId={`${testId}-time`}
            />
            <Stat
              label={t("priority_recommendation.confidence")}
              value={`${recommendation.confidence_pct || 80} %`}
              tone="info"
              icon={Target}
              testId={`${testId}-confidence`}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleClick}
          data-testid={`${testId}-cta`}
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white shadow-sm transition-colors ${
            isCritical
              ? "bg-rose-500 hover:bg-rose-600"
              : "bg-amber-500 hover:bg-amber-600"
          }`}
        >
          {t("priority_recommendation.cta")}
          <ArrowRight size={14} />
        </button>
      </div>
    </section>
  );
}

function Stat({ label, value, tone, icon: Icon, testId }) {
  const valueColor = {
    warning: "text-rose-600",
    info: "text-emerald-600",
    neutral: "text-qenva-navy",
  }[tone || "neutral"];
  return (
    <div data-testid={testId} className="flex items-baseline gap-1.5">
      <span className="text-zinc-500 font-semibold inline-flex items-center gap-1">
        {Icon && <Icon size={11} className="text-zinc-400" />}
        {label} :
      </span>
      <span className={`font-bold tabular-nums ${valueColor}`}>{value}</span>
    </div>
  );
}
